import { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faLock, faPlane, faCartShopping, faTruckFast, faMobileScreen, faCubes } from '@fortawesome/free-solid-svg-icons';

import SpotlightCard from './ui/SpotlightCard';

import '../styles/ProjectCard.scss';

const CATEGORY_ICONS = {
  Aviation: faPlane,
  'E-commerce': faCartShopping,
  Logistics: faTruckFast,
  Mobile: faMobileScreen,
  Platforms: faCubes,
};

function isLiveUrl(href) {
  return typeof href === 'string' && /^https?:\/\//i.test(href);
}

/** Splits "Name - tagline" headings into a title and subtitle. */
function splitHeading(heading) {
  const idx = heading.indexOf(' - ');
  if (idx === -1) return [heading, ''];
  return [heading.slice(0, idx), heading.slice(idx + 3)];
}

const ProjectCard = ({ cardContent, cardIndex = 0, order = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const hasLiveLink = isLiveUrl(cardContent.buttonLink);
  const tags = (Array.isArray(cardContent.tags) ? cardContent.tags : []).filter((t) => t !== 'Internal');
  const categories = cardContent.categories || [];
  const primary = categories[0];
  const [title, subtitle] = splitHeading(cardContent.heading);
  const descId = `project-desc-${cardIndex}`;

  return (
    <article
      className={`project-card project-card--${(primary || 'default').toLowerCase().replace(/[^a-z]/g, '')}`}
      style={{ '--order': order }}
    >
      <SpotlightCard tilt className="project-card__inner">
        <div className="project-card__head">
          <span className="project-card__icon" aria-hidden="true">
            <FontAwesomeIcon icon={CATEGORY_ICONS[primary] || faCubes} />
          </span>
          <span className="project-card__index" aria-hidden="true">
            {String(cardIndex + 1).padStart(2, '0')}
          </span>
        </div>

        <ul className="project-card__cats" aria-label="Category">
          {categories.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>

        <h3 className="project-card__title">{title}</h3>
        {subtitle && <p className="project-card__subtitle">{subtitle}</p>}

        <p
          id={descId}
          className={`project-card__desc${expanded ? ' project-card__desc--open' : ''}`}
        >
          {cardContent.description}
        </p>
        <button
          type="button"
          className="project-card__toggle"
          aria-expanded={expanded}
          aria-controls={descId}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>

        {tags.length > 0 && (
          <ul className="project-card__tags" aria-label="Technologies">
            {tags.map((tag) => (
              <li key={tag} className="chip">
                {tag}
              </li>
            ))}
          </ul>
        )}

        <div className="project-card__footer">
          {hasLiveLink ? (
            <a
              className="project-card__cta"
              href={cardContent.buttonLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Visit ${title} (opens in a new tab)`}
            >
              Visit live <FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden="true" />
            </a>
          ) : (
            <span className="project-card__cta project-card__cta--internal" title={cardContent.buttonText}>
              <FontAwesomeIcon icon={faLock} aria-hidden="true" /> Internal enterprise system
            </span>
          )}
        </div>
      </SpotlightCard>
    </article>
  );
};

export default ProjectCard;
